import { z } from 'zod';
import { Timestamp, Uuid } from './common.js';

/**
 * **A protection account** — one engagement a security firm is on (security
 * fold, 2026-09-22).
 *
 * This file is the whole of the accounts API: create one, list them, read one,
 * change one. It used to carry a great deal more — engagement roles, an
 * account-subject roster, an identity-only directory, a context-change inbox
 * and an account-scoped knowledge search — because a security org's records
 * lived in a parallel substrate with parallel read surfaces over it. Those
 * surfaces are the standard ones narrowed by `account_id` now: the roster is
 * `GET /subjects?account_id=…`, the directory is the People and Organizations
 * lists, and the search is `GET /knowledge?account_id=…`.
 *
 * Membership is not modeled here either. An entity's account is
 * `entity.security_account_id` and nothing else — a column, a trigger and a
 * wall — so there is no roster row to carry roles or relevance prose, and the
 * relationship a record has to its engagement is its `status` like every other
 * vertical's.
 */
export const SecurityAccountState = z.enum(['active', 'archived']);

// PostgreSQL text cannot store NUL, even though JSON can represent it.
const AccountText = z
  .string()
  .trim()
  .refine((value) => !value.includes('\u0000'), 'Text cannot contain null characters.');
const Label = AccountText.min(1).max(200);
const Context = AccountText.max(20_000);
const Version = z.number().int().min(1).max(2_147_483_647);

export const CreateSecurityAccountBody = z
  .object({
    label: Label,
    engagement_context: Context.default(''),
  })
  .strict();
export type CreateSecurityAccountBody = z.infer<typeof CreateSecurityAccountBody>;

/** The version guards the account's own context, so a stale editor cannot overwrite it. */
export const UpdateSecurityAccountBody = z
  .object({
    expected_version: Version,
    label: Label.optional(),
    engagement_context: Context.optional(),
    state: SecurityAccountState.optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.label !== undefined || body.engagement_context !== undefined || body.state !== undefined,
    'Supply at least one account change.',
  );
export type UpdateSecurityAccountBody = z.infer<typeof UpdateSecurityAccountBody>;

export const ListSecurityAccountsQuery = z
  .object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  })
  .strict()
  .extend({
    state: z.enum(['active', 'archived', 'all']).default('active'),
    q: z.string().trim().max(200).optional(),
  });
export type ListSecurityAccountsQuery = z.infer<typeof ListSecurityAccountsQuery>;

export const SecurityAccountView = z.object({
  id: Uuid,
  label: z.string(),
  engagement_context: z.string(),
  state: SecurityAccountState,
  version: Version,
  created_by_user_id: Uuid,
  updated_by_user_id: Uuid,
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type SecurityAccountView = z.infer<typeof SecurityAccountView>;

export const SecurityAccountResponse = z.object({ account: SecurityAccountView });
export type SecurityAccountResponse = z.infer<typeof SecurityAccountResponse>;

/**
 * **How much is on this account**, answered by `GET /security/accounts/:id`
 * alone.
 *
 * Derived from `entity.security_account_id` rather than stored, because the
 * column is the membership: there is no roster table to count and no second
 * number that could disagree with the People and Organizations lists. Live rows
 * only — a deleted record is not on the account and a merged-away one is the
 * record it became.
 *
 * Not on {@link SecurityAccountView}, so the list does not pay for it: the list
 * is a page of accounts and counting each one's records would be a query per
 * row for a number nobody reads there.
 */
export const SecurityAccountRecordCounts = z.object({
  people: z.number().int().nonnegative(),
  organizations: z.number().int().nonnegative(),
});
export type SecurityAccountRecordCounts = z.infer<typeof SecurityAccountRecordCounts>;

export const SecurityAccountDetailResponse = z.object({
  account: SecurityAccountView,
  records: SecurityAccountRecordCounts,
});
export type SecurityAccountDetailResponse = z.infer<typeof SecurityAccountDetailResponse>;

export const SecurityAccountListResponse = z.object({
  accounts: z.array(SecurityAccountView),
  total: z.number().int().min(0),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type SecurityAccountListResponse = z.infer<typeof SecurityAccountListResponse>;
